import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Save, TestTube, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PrinterSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [settings, setSettings] = useState({
    packing_slip_printer_ip: '',
    packing_slip_printer_port: '9100',
    auto_print_enabled: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/settings/printer');
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching printer settings:', error);
      toast.error('Failed to load printer settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await apiClient.post('/admin/settings/printer', settings);
      if (response.data.success) {
        toast.success('Printer settings saved successfully');
      } else {
        toast.error(response.data.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving printer settings:', error);
      toast.error('Failed to save printer settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!settings.packing_slip_printer_ip) {
      toast.error('Please enter a printer IP address');
      return;
    }
    
    try {
      setTesting(true);
      setTestResult(null);
      
      const response = await apiClient.post('/admin/settings/printer/test', {
        printer_ip: settings.packing_slip_printer_ip,
        printer_port: settings.packing_slip_printer_port
      });
      
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error testing printer:', error);
      setTestResult({ success: false, message: 'Connection test failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#8B4513]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B4513] to-[#A0522D] text-white p-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-white hover:bg-white/20"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Printer className="w-6 h-6" />
                Printer Settings
              </h1>
              <p className="text-sm text-white/80">Configure network printers for automatic printing</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Packing Slip Printer */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5" />
              Packing Slip Printer
            </CardTitle>
            <CardDescription>
              Configure the network printer for automatic packing slip printing when all boxes are packed.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Auto Print Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Auto-Print</Label>
                <p className="text-sm text-gray-500">
                  Automatically print all packing slips when the last box is packed
                </p>
              </div>
              <Switch
                checked={settings.auto_print_enabled}
                onCheckedChange={(checked) => setSettings({ ...settings, auto_print_enabled: checked })}
                data-testid="auto-print-toggle"
              />
            </div>

            {/* Printer IP */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="printer-ip">Printer IP Address</Label>
                <Input
                  id="printer-ip"
                  placeholder="e.g., 192.168.1.100"
                  value={settings.packing_slip_printer_ip}
                  onChange={(e) => setSettings({ ...settings, packing_slip_printer_ip: e.target.value })}
                  data-testid="printer-ip-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="printer-port">Port</Label>
                <Input
                  id="printer-port"
                  placeholder="9100"
                  value={settings.packing_slip_printer_port}
                  onChange={(e) => setSettings({ ...settings, packing_slip_printer_port: e.target.value })}
                  data-testid="printer-port-input"
                />
                <p className="text-xs text-gray-500">Default: 9100 (RAW printing)</p>
              </div>
            </div>

            {/* Test Connection */}
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testing || !settings.packing_slip_printer_ip}
                  data-testid="test-connection-btn"
                >
                  {testing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
              </div>
              
              {testResult && (
                <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className={`flex items-start gap-2 text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.success ? (
                      <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p>{testResult.message}</p>
                      {testResult.is_network_issue && (
                        <p className="mt-2 text-xs text-red-600">
                          <strong>Workaround:</strong> You can still download and print PDFs manually from the packing workstation.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <Printer className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-900">How Auto-Print Works</h4>
                <ul className="mt-2 text-sm text-blue-700 space-y-1">
                  <li>• When all items in a pick slip are packed into boxes, printing is triggered automatically</li>
                  <li>• All box packing slips for the completed pick slip will be sent to the printer</li>
                  <li>• Ensure the printer is connected to the network and powered on</li>
                  <li>• Use port 9100 for RAW printing (supported by most HP/Canon printers)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Network Limitation Notice */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <div className="flex-shrink-0">
                <XCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-medium text-amber-900">Network Limitation</h4>
                <p className="mt-2 text-sm text-amber-700">
                  Auto-print only works when the server and printer are on the same network. For cloud deployments, 
                  the server cannot reach printers on your local network (192.168.x.x addresses).
                </p>
                <p className="mt-2 text-sm text-amber-700">
                  <strong>Alternative:</strong> Use the "Download PDF" or "Print" buttons in the packing workstation to manually print packing slips.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#8B4513] hover:bg-[#A0522D]"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrinterSettings;
